import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { fetchMySchools, normalizeMySchoolsList } from "../../lib/schools";

const ROLE_BADGE = {
  admin: {
    label: "Admin",
    className: "bg-blush/70 text-[#6b3a55]",
  },
  instructor: {
    label: "Instructor",
    className: "bg-orchid/45 text-[#5a3d7a]",
  },
};

/**
 * Lightweight "My Schools" links under the /events greeting.
 * Hidden when the user has no school memberships or follows.
 */
export default function FeedMySchools() {
  const { account } = useAuth();
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    if (!account) {
      setSchools([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMySchools();
        if (!cancelled) setSchools(normalizeMySchoolsList(data));
      } catch {
        if (!cancelled) setSchools([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account]);

  if (schools.length === 0) return null;

  return (
    <nav
      className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 pt-3 pb-0.5 font-ui text-[13px] leading-snug"
      aria-label="My schools"
      data-testid="feed-my-schools"
    >
      <span className="font-medium text-ink/55 shrink-0">My Schools:</span>
      <ul className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 list-none m-0 p-0 min-w-0">
        {schools.map((school, index) => {
          const badge = ROLE_BADGE[school.role];
          return (
            <li key={school.id} className="inline-flex items-baseline gap-1 min-w-0">
              {index > 0 && (
                <span className="text-ink/30 select-none" aria-hidden>
                  ·
                </span>
              )}
              <Link
                to={school.role === "admin" ? `/admin/${school.slug}` : `/school/${school.slug}`}
                className="feed-school-pill font-medium text-ink no-underline hover:text-plum transition-colors truncate max-w-[14rem] sm:max-w-[18rem]"
                data-testid={`feed-my-school-${school.slug}`}
              >
                {school.name}
              </Link>
              {badge && (
                <span
                  className={`feed-role-badge inline-flex items-center rounded-btn px-1.5 py-px text-[11px] font-semibold leading-tight shrink-0 ${badge.className}`}
                >
                  {badge.label}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
